import { AI_API_URL } from '@/lib/constants'

/**
 * Constants for the AIR® AI side panel.
 *
 * User-facing name: **AIR® AI**. Internal code uses `ai`. Do not leak "Query Agent" / "Analyst" anywhere a user can see.
 */

/** Backend API base — the Noah service (separate host from the AIR® REST API). */
export const API_BASE = AI_API_URL

export const API_PATHS = {
	chat: '/api/chat',
	betaChat: '/api/beta/chat',
	sessions: '/api/sessions',
	session: (id: string) => `/api/sessions/${id}`,
	betaSession: (id: string) => `/api/beta/sessions/${id}`,
} as const

/** AI model variants exposed to the user. Maps to a chat endpoint. */
export type AIModel = 'standard' | 'beta'

export const DEFAULT_AI_MODEL: AIModel = 'standard'

export const AI_MODEL_LABELS: Record<AIModel, string> = {
	standard: 'Standard',
	beta: 'Beta',
}

/**
 * PostHog event names emitted by the panel. Keep in sync with the
 * Observability decision in `ai-panel/PLAN.md`.
 */
export const POSTHOG_EVENTS = {
	panelOpened: 'ai_panel_opened',
	messageSent: 'ai_message_sent',
	messageCompleted: 'ai_message_completed',
	streamStopped: 'ai_stream_stopped',
	openInSearchClicked: 'ai_open_in_search_clicked',
	sessionResumed: 'ai_session_resumed',
	error: 'ai_error',
	/**
	 * Fired once per (sessionId, toolName) when the backend streams a tool
	 * we don't have an entry for in `tool-registry.ts`. Stand-in for the
	 * missing backend `friendly_name` / `category` fields — gives us an
	 * early-warning signal that the registry needs an update.
	 */
	unknownTool: 'ai_unknown_tool',
} as const

/** Maximum number of recent sessions shown in the empty-state preview. */
export const DEFAULT_RECENT_BUBBLE_LIMIT = 3

/** Fallback title rendered when a Session has no server-provided title. */
export const SESSION_TITLE_FALLBACK = 'New Conversation'
